import type { ConfirmTrialResult } from '~/gql/trial/trial'
import { useMutation } from '@apollo/client/react'
import { CheckCircle2, Clock, MailWarning, XCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import { BrandLogo } from '~/components/BrandLogo'
import { MAIN_ID } from '~/components/SkipLink'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { GQL_CONFIRM_TRIAL } from '~/gql/trial/trial'

/**
 * Confirmation de l'adresse d'un essai (#155), atterrissage du lien reçu par mail.
 *
 * Confirmer ne crée plus l'espace : la demande entre en file de revue plateforme.
 * Cet écran affiche donc l'état de cette revue — en attente, approuvée (un e-mail
 * d'activation part alors), ou refusée (avec le motif). Un lien expiré ou invalide
 * affiche un message d'erreur.
 */
type Phase = 'loading' | 'expired' | 'error' | 'done'

/**
 * Quitte le basename courant : navigation dure. Les cibles visées d'ici sont soit
 * l'espace d'une autre org (`/o/<slug>/login`), soit la route plateforme servie à
 * la racine (`/signup`, #37) — react-router préfixerait les deux.
 */
function goTo(path: string) {
  window.location.assign(path)
}

function ConfirmTrial() {
  const { t } = useTranslation()
  const token = new URLSearchParams(window.location.search).get('token')
  const [phase, setPhase] = useState<Phase>('loading')
  const [result, setResult] = useState<ConfirmTrialResult | null>(null)
  const [confirmTrial] = useMutation(GQL_CONFIRM_TRIAL)
  const started = useRef(false)

  useEffect(() => {
    if (!token || started.current) return
    started.current = true

    confirmTrial({ variables: { token } })
      .then(({ data }) => {
        const res = data?.confirmTrialOrganization as unknown as ConfirmTrialResult | undefined
        if (!res) {
          setPhase('error')
          return
        }
        setResult(res)
        setPhase('done')
      })
      .catch((err) => {
        setPhase(String(err).includes('EXPIRED_TOKEN') ? 'expired' : 'error')
      })
  }, [token, confirmTrial])

  if (!token) return <Navigate to="/login" replace />

  const status = result?.status

  return (
    <main id={MAIN_ID} className="flex w-full min-h-dvh flex-col items-center justify-center p-5">
      <Card className="sm:w-md w-full">
        <CardHeader className="gap-4">
          <BrandLogo className="h-9 w-auto" fallbackClassName="text-lg" />
          <CardTitle as="h1" className="font-heading text-2xl tracking-tight">
            {t('confirmTrial.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {phase === 'loading' && (
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              <Spinner className="size-8 text-primary-text" label={t('confirmTrial.loading')} />
              <p aria-hidden className="text-sm text-muted-foreground">{t('confirmTrial.loading')}</p>
            </div>
          )}

          {phase === 'done' && status === 'PENDING' && (
            <div className="flex flex-col items-center gap-4 text-center">
              <Clock className="size-12 text-primary-text" />
              <p className="text-sm text-muted-foreground">{t('confirmTrial.pending')}</p>
            </div>
          )}

          {phase === 'done' && status === 'APPROVED' && (
            <div className="flex flex-col items-center gap-4 text-center">
              <CheckCircle2 className="size-12 text-primary-text" />
              <p className="text-sm text-muted-foreground">{t('confirmTrial.approved')}</p>
              {result?.slug && (
                <Button variant="outline" onClick={() => goTo(`/o/${result.slug}/login`)} className="w-full">
                  {t('confirmTrial.goToLogin')}
                </Button>
              )}
            </div>
          )}

          {phase === 'done' && status === 'REJECTED' && (
            <div className="flex flex-col items-center gap-4 text-center">
              <XCircle className="size-12 text-destructive" />
              <p className="text-sm text-muted-foreground">{t('confirmTrial.rejected')}</p>
              {result?.rejectionReason && (
                <p className="rounded-md bg-muted/60 px-3 py-2 text-sm">
                  <span className="font-medium">{t('confirmTrial.reason')}</span>
                  {' '}
                  {result.rejectionReason}
                </p>
              )}
            </div>
          )}

          {(phase === 'expired' || phase === 'error') && (
            <div className="flex flex-col items-center gap-4 text-center">
              <MailWarning className="size-10 text-destructive" />
              <p className="text-sm text-muted-foreground">
                {t(phase === 'expired' ? 'confirmTrial.expired' : 'confirmTrial.error')}
              </p>
              <Button variant="outline" onClick={() => goTo('/signup')} className="w-full">
                {t('confirmTrial.restart')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  )
}

export default ConfirmTrial
